import { getAddress } from '@ethersproject/address'
import { AddressZero } from '@ethersproject/constants'
import { Contract } from '@ethersproject/contracts'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import BigNumber from 'bignumber.js'
import { JSEncrypt } from 'jsencrypt'
import { TokenModel } from 'services/types/Application'
import { CoinModel } from 'services/types/User'
const numbro = require('numbro')

const SHA256 = require('crypto-js/sha256')

BigNumber.config({ EXPONENTIAL_AT: [-40, 40] })
// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

export function bcryptPassword(password: string, publicKey: string): string {
  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(publicKey)
  return encrypt.encrypt(SHA256(password).toString()) || ''
}

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

// account is not optional
function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
function getProviderOrSigner(library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

export function getContract(address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(address, ABI, getProviderOrSigner(library, account) as any)
}

export function getDefaultProvider(): Web3Provider | undefined {
  if (window.ethereum) {
    return new Web3Provider(window.ethereum)
  } else {
    return undefined
  }
}

export function numFormat(value?: string | number, thousandSeparated = true): string {
  if (value) {
    const fmt = {
      prefix: '',
      decimalSeparator: '.',
      groupSeparator: ',',
      groupSize: thousandSeparated ? 3 : 0,
      secondaryGroupSize: 0,
      fractionGroupSeparator: ' ',
      fractionGroupSize: 0,
      suffix: '',
    }
    const x = new BigNumber(value)
    BigNumber.config({ FORMAT: fmt })
    return x.toFormat()
  }
  return ''
}

export function fixedFormat(value?: string | number): string {
  if (value) {
    if (value.toString().split('.')[1] && value.toString().split('.')[1].length > 8) {
      return new BigNumber(value).dp(8).toFormat(2, 1)
    } else {
      return new BigNumber(value).toFormat(2, 1)
    }
  }
  return ''
}

export function decimalFormat(value?: number | string | null, decimal = 18, thousandSeparated = true, precision = 2) {
  if (value && value !== null) {
    if (thousandSeparated) {
      return new BigNumber(value).div(new BigNumber(10).pow(decimal)).toFormat(precision, 1)
    } else {
      return new BigNumber(value).div(new BigNumber(10).pow(decimal)).toFixed(precision, 1).toString()
    }
  } else {
    return '0'
  }
}

export function div(value1: number | string | BigNumber, value2: number | string | BigNumber) {
  if (value1) {
    if (BigNumber.isBigNumber(value1)) {
      return value1.div(value2).toFormat()
    } else {
      return new BigNumber(value1).div(value2).toFormat()
    }
  } else {
    return '0'
  }
}

export function timestampFormat(diff: number): [number, number, number, number] {
  if (diff > 0) {
    const diffd = Math.floor(diff / (24 * 3600 * 1000))
    const diffh = Math.floor((diff / (1000 * 60 * 60)) % 24)
    const diffm = Math.floor((diff / (1000 * 60)) % 60)
    const diffs = Math.floor((diff / 1000) % 60)
    return [diffd, diffh, diffm, diffs]
  } else {
    return [0, 0, 0, 0]
  }
}

export function desensitization(str: string | undefined | null, beginLen = 4, endLen = -4, fillingLen = 4) {
  if (str === 'null') return 'null'
  if (!str) return ''
  let tempStr = ''
  const len = str.length
  const firstStr = str.substring(0, beginLen)
  const lastStr = str.substring(len + endLen, len)
  const middleStr = str.substring(beginLen, beginLen + fillingLen).replace(/[\s\S]/gi, '.')
  tempStr = firstStr + middleStr + lastStr
  return tempStr
}
export const isValidEmail = (email: string) => {
  const res = email.match(/^[/+_a-z0-9-'&=]+(\.[/+_a-z0-9-']+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})$/i)
  return res !== null
}

export const repairZero = (value: string | number, length: number) => {
  return (Array(length).join('0') + value).slice(-length)
}

export const formatFiatPrice = (value: string | number, price: string | number): string | number => {
  if (!price) {
    return value
  }
  if (!value || value.toString().indexOf('-') > -1) {
    return '--'
  }
  if (value && price) {
    return new BigNumber(value).times(price).toFixed(2, 1).toString()
  }
  return '--'
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function windowWidthImage(url: string) {
  const width = document.body.clientWidth
  if (width >= 1920) {
    return convertImage(url, 1920, 0)
  } else if (width >= 1200) {
    return convertImage(url, 1200, 0)
  } else if (width >= 900) {
    return convertImage(url, 900, 0)
  } else if (width >= 600) {
    return convertImage(url, 600, 0)
  }
  return convertImage(url, 1200, 0)
}

export function convertImage(url: string, width: number, height: number, to?: string) {
  if (!url) return ''
  const urlSplitLast = url.split('.')
  let name = `${urlSplitLast[urlSplitLast.length - 2]}_${width}_${height}`
  if (to !== undefined) {
    name += `_${urlSplitLast[urlSplitLast.length - 1]}`
    urlSplitLast[urlSplitLast.length - 1] = to
  }
  urlSplitLast[urlSplitLast.length - 2] = name
  return urlSplitLast.join('.')
}

export function getTokenDecimalNum(tokenList: Array<TokenModel>, symbolId: number): number {
  if (tokenList.length > 0 && symbolId) {
    const token = tokenList.find(el => el.id === symbolId)
    return token ? token.decimalNum : 18
  }
  return 18
}

export function getTokenSymbol(tokenList: Array<TokenModel>, coinId: number): string {
  if (tokenList.length > 0 && coinId) {
    const token = tokenList.find(el => el.id === Number(coinId))
    return token ? token.symbol : ''
  }
  return ''
}

export function getTokenIcon(tokenList: Array<TokenModel>, coinId: number): string {
  if (tokenList.length > 0 && coinId) {
    const token = tokenList.find(el => el.id === Number(coinId))
    return token ? token.icon : ''
  }
  return ''
}
export function getToken(tokenList: Array<TokenModel>, coinId: number) {
  if (tokenList.length > 0 && coinId) {
    const token = tokenList.find(el => el.id === Number(coinId))
    return token
  }
  return null
}

export function getUserBalance(list: CoinModel[], coinId: number): string {
  if (list.length > 0 && coinId) {
    const coin = list.find(el => el.coinId === Number(coinId))
    return coin ? coin.amount : '0'
  }
  return '0'
}

export function numberAverage(num: string) {
  return num ? numbro(num).format({ average: true }) : '--'
}

export function swapArr(arr: any, index1: number, index2: number) {
  arr[index1] = arr.splice(index2, 1, arr[index1])[0]
  return arr
}

export function shuffle(arr: any) {
  let n = arr.length,
    random
  while (0 !== n) {
    random = (Math.random() * n--) >>> 0
    ;[arr[n], arr[random]] = [arr[random], arr[n]]
  }
  return arr
}

export function isUrl(url: string) {
  return /^https?:\/\/([a-zA-Z0-9]+\.)+[a-zA-Z0-9]+/.test(url)
}

export function unique(arr: any[]) {
  return Array.from(new Set(arr))
}

export function dataURLtoFile(url: string, fileName: string) {
  const arr = url.split(',')
  if (arr.length < 2) {
    return undefined
  }
  const mimeArr = arr[0].match(/:(.*?);/)
  if (!mimeArr || mimeArr.length < 2) {
    return undefined
  }
  const mime = mimeArr[1]
  const buff = Buffer.from(arr[1], 'base64')
  return new File([buff], fileName, { type: mime })
}
